import { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { ArrowLeft, Upload, Check, Loader2 } from "lucide-react";
import { Logo, PrimaryButton, FieldGroupLabel } from "@/components/brand";
import GoogleButton from "@/components/GoogleButton";
import { ONTARIO_REGIONS, LICENCE_CLASSES, VEHICLE_TYPES } from "@/constants/routa";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import Footer from "@/components/Footer";

const PANEL_IMG = "https://images.unsplash.com/photo-1670121180530-cfcba4438038?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000";

function Field({ label, children }) {
  return (
    <div>
      <label className="routa-label">{label}</label>
      {children}
    </div>
  );
}

function DocUpload({ type, label, onUploaded, uploaded }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const handle = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    setErr("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/applications/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onUploaded({ type, file_id: data.file_id, filename: data.filename });
    } catch (e2) {
      setErr(formatApiError(e2.response?.data?.detail) || "Upload failed");
    }
    setBusy(false);
  };
  return (
    <label className="flex items-center justify-between border border-gray-200 px-4 py-3 cursor-pointer hover:border-[#0a0a0a] transition-colors" data-testid={`doc-upload-${type}`}>
      <div className="flex items-center gap-3">
        {uploaded ? <Check className="h-4 w-4 text-orange-500" /> : busy ? <Loader2 className="h-4 w-4 animate-spin text-gray-400" /> : <Upload className="h-4 w-4 text-gray-400" />}
        <div>
          <div className="text-sm text-[#0a0a0a]">{label}</div>
          {uploaded && <div className="text-xs text-gray-500">{uploaded.filename}</div>}
          {err && <div className="text-xs text-orange-600">{err}</div>}
        </div>
      </div>
      <span className="font-mono text-[10px] tracking-[0.14em] uppercase text-gray-400">{uploaded ? "Replace" : "Upload"}</span>
      <input type="file" className="hidden" accept=".pdf,.png,.jpg,.jpeg" onChange={handle} />
    </label>
  );
}

const empty = {
  first_name: "", last_name: "", email: "", phone: "", password: "",
  city: "", province: "Ontario", region: "Toronto",
  licence_number: "", licence_class: "G", licence_expiry: "",
  vehicle_make: "", vehicle_model: "", vehicle_year: "", vehicle_plate: "", vehicle_type: "Cargo van",
  insurance_provider: "", insurance_policy: "", insurance_expiry: "",
  background_consent: false,
};

export default function Apply() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const gt = params.get("gt");
  const { setSession } = useAuth();
  const [form, setForm] = useState(empty);
  const [docs, setDocs] = useState({});
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  // Prefill from Google signup token (single-use, 15 min).
  useEffect(() => {
    if (!gt) return;
    (async () => {
      try {
        const { data } = await api.get(`/auth/google/session/${encodeURIComponent(gt)}`);
        const [first_name, ...rest] = (data.name || "").split(" ");
        setForm((f) => ({ ...f, email: data.email, first_name: first_name || f.first_name, last_name: rest.join(" ") || f.last_name }));
      } catch (e) {
        setError(formatApiError(e.response?.data?.detail) || "Google sign-in expired. Please try again.");
      }
    })();
  }, [gt]);

  const onDoc = (d) => setDocs((prev) => ({ ...prev, [d.type]: d }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.background_consent) return setError("You must provide background check consent to continue.");
    setBusy(true);
    try {
      const payload = { ...form, documents: Object.values(docs) };
      if (gt) {
        delete payload.password;
        payload.google_signup_token = gt;
      }
      const { data } = await api.post("/applications", payload);
      setSession(data.user, data.token);
      navigate("/application-submitted", { replace: true });
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    }
    setBusy(false);
  };

  return (
    <div className="min-h-screen bg-white">
      <header className="border-b border-gray-200 sticky top-0 bg-white/90 backdrop-blur z-40">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 h-16 flex items-center justify-between">
          <Logo />
          <div className="flex items-center gap-7">
            <Link to="/login?role=driver" className="font-mono text-[11px] tracking-[0.14em] uppercase text-gray-500 hover:text-[#0a0a0a]">Driver login</Link>
            <PrimaryButton className="px-4 py-2 text-[13px]" onClick={() => navigate("/apply")}>Apply to drive</PrimaryButton>
          </div>
        </div>
      </header>

      <div className="grid lg:grid-cols-2">
        {/* Form */}
        <div className="px-6 lg:px-12 py-12 max-w-2xl">
          <Link to="/" className="inline-flex items-center gap-2 text-sm text-gray-600 hover:text-[#0a0a0a] mb-10">
            <ArrowLeft className="h-4 w-4" /> Back
          </Link>
          <div className="font-mono text-[11px] tracking-[0.18em] uppercase text-gray-400 mb-4">[02] Driver Application</div>
          <h1 className="headline text-4xl lg:text-5xl text-[#0a0a0a]">
            Become a Routa <span className="text-orange-500">approved operator.</span>
          </h1>
          <p className="mt-5 text-base text-gray-500 max-w-md">
            All applications are reviewed by our operations team. We will email you when a decision is made.
          </p>

          <form onSubmit={submit} className="mt-12 space-y-14">
            {error && <div data-testid="apply-error" className="border border-orange-500 bg-orange-50 text-orange-700 text-sm px-4 py-3">{error}</div>}

            {/* 01 Operator identity */}
            <section>
              <FieldGroupLabel number="01" label="Operator identity" />
              {gt && (
                <div className="mb-5 border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-600" data-testid="apply-google-banner">
                  Applying with Google as <span className="text-[#0a0a0a] font-semibold">{form.email}</span>. No password needed.
                </div>
              )}
              <div className="grid sm:grid-cols-2 gap-5">
                <Field label="First name"><input data-testid="apply-first-name" required value={form.first_name} onChange={set("first_name")} className="routa-input" /></Field>
                <Field label="Last name"><input data-testid="apply-last-name" required value={form.last_name} onChange={set("last_name")} className="routa-input" /></Field>
                <Field label="Email"><input data-testid="apply-email" type="email" required disabled={!!gt} value={form.email} onChange={set("email")} className="routa-input disabled:bg-gray-50 disabled:text-gray-500" /></Field>
                <Field label="Phone number"><input data-testid="apply-phone" required value={form.phone} onChange={set("phone")} className="routa-input" /></Field>
                {!gt && <Field label="Create portal password"><input data-testid="apply-password" type="password" minLength={6} required value={form.password} onChange={set("password")} className="routa-input" /></Field>}
              </div>
              {!gt && (
                <div className="mt-6">
                  <div className="flex items-center gap-4 mb-4">
                    <span className="h-px flex-1 bg-gray-200" />
                    <span className="font-mono text-[10px] tracking-[0.18em] uppercase text-gray-400">or</span>
                    <span className="h-px flex-1 bg-gray-200" />
                  </div>
                  <GoogleButton intent="driver" label="Apply with Google" />
                </div>
              )}
            </section>

            {/* 02 Region */}
            <section>
              <FieldGroupLabel number="02" label="Region" />
              <div className="grid sm:grid-cols-2 gap-5">
                <Field label="City"><input data-testid="apply-city" required value={form.city} onChange={set("city")} className="routa-input" /></Field>
                <Field label="Province"><input data-testid="apply-province" value={form.province} onChange={set("province")} className="routa-input" /></Field>
                <Field label="Service region">
                  <select data-testid="apply-region" value={form.region} onChange={set("region")} className="routa-input">
                    {ONTARIO_REGIONS.map((r) => <option key={r} value={r}>{r}</option>)}
                  </select>
                </Field>
              </div>
            </section>

            {/* 03 Licence */}
            <section>
              <FieldGroupLabel number="03" label="Driver's licence" />
              <div className="grid sm:grid-cols-2 gap-5">
                <Field label="Driver's licence number"><input data-testid="apply-licence-number" required value={form.licence_number} onChange={set("licence_number")} className="routa-input" /></Field>
                <Field label="Licence class">
                  <select data-testid="apply-licence-class" value={form.licence_class} onChange={set("licence_class")} className="routa-input">
                    {LICENCE_CLASSES.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </Field>
                <Field label="Licence expiry"><input data-testid="apply-licence-expiry" type="date" required value={form.licence_expiry} onChange={set("licence_expiry")} className="routa-input" /></Field>
              </div>
            </section>

            {/* 04 Vehicle */}
            <section>
              <FieldGroupLabel number="04" label="Vehicle details" />
              <div className="grid sm:grid-cols-2 gap-5">
                <Field label="Make"><input data-testid="apply-vehicle-make" required value={form.vehicle_make} onChange={set("vehicle_make")} className="routa-input" /></Field>
                <Field label="Model"><input data-testid="apply-vehicle-model" required value={form.vehicle_model} onChange={set("vehicle_model")} className="routa-input" /></Field>
                <Field label="Year"><input data-testid="apply-vehicle-year" required value={form.vehicle_year} onChange={set("vehicle_year")} className="routa-input" /></Field>
                <Field label="Plate"><input data-testid="apply-vehicle-plate" required value={form.vehicle_plate} onChange={set("vehicle_plate")} className="routa-input" /></Field>
                <Field label="Vehicle type">
                  <select data-testid="apply-vehicle-type" value={form.vehicle_type} onChange={set("vehicle_type")} className="routa-input">
                    {VEHICLE_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                </Field>
              </div>
            </section>

            {/* 05 Insurance & documents */}
            <section>
              <FieldGroupLabel number="05" label="Insurance & documents" />
              <div className="grid sm:grid-cols-2 gap-5 mb-6">
                <Field label="Insurance provider"><input data-testid="apply-insurance-provider" required value={form.insurance_provider} onChange={set("insurance_provider")} className="routa-input" /></Field>
                <Field label="Policy number"><input data-testid="apply-insurance-policy" required value={form.insurance_policy} onChange={set("insurance_policy")} className="routa-input" /></Field>
                <Field label="Insurance expiry"><input data-testid="apply-insurance-expiry" type="date" required value={form.insurance_expiry} onChange={set("insurance_expiry")} className="routa-input" /></Field>
              </div>
              <div className="space-y-3">
                <DocUpload type="licence" label="Driver's licence (front & back)" uploaded={docs.licence} onUploaded={onDoc} />
                <DocUpload type="insurance" label="Insurance certificate" uploaded={docs.insurance} onUploaded={onDoc} />
                <DocUpload type="registration" label="Vehicle registration" uploaded={docs.registration} onUploaded={onDoc} />
              </div>
            </section>

            {/* 06 Background consent */}
            <section>
              <FieldGroupLabel number="06" label="Background consent" />
              <label className="flex items-start gap-3 cursor-pointer">
                <input data-testid="apply-consent" type="checkbox" checked={form.background_consent} onChange={(e) => setForm((f) => ({ ...f, background_consent: e.target.checked }))} className="mt-1 h-4 w-4 accent-orange-500" />
                <span className="text-sm text-gray-600">
                  I authorize Routa Logistics Inc. to verify my provincial driving record, licence status, and conduct a background check as part of the operator review process.
                </span>
              </label>
            </section>

            <div className="pt-2">
              <PrimaryButton orange arrow data-testid="apply-submit" type="submit" disabled={busy} className="w-full sm:w-auto">
                {busy ? "Submitting…" : "Submit application"}
              </PrimaryButton>
              <p className="mt-4 text-xs text-gray-400">Apply once. Get reviewed within 48 hours.</p>
            </div>
          </form>
        </div>

        {/* Image panel */}
        <div className="hidden lg:block relative">
          <div className="sticky top-16 h-[calc(100vh-4rem)]">
            <img src={PANEL_IMG} alt="Logistics" className="absolute inset-0 h-full w-full object-cover" />
            <div className="absolute inset-x-0 bottom-0 h-56 bg-gradient-to-t from-black/85 to-transparent" />
            <div className="absolute bottom-0 left-0 right-0 p-10">
              <div className="font-mono text-[10px] tracking-[0.18em] uppercase text-white/50 mb-3">Scope</div>
              <p className="text-2xl font-bold text-white leading-tight max-w-sm">
                A regulated foundation for the next generation of <span className="text-orange-500">delivery operators.</span>
              </p>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}
